import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Image as ImageIcon, Settings, Trash2 , Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FlowNodeProps, ContentBlock, ImageAspectRatio } from '@/types/flow';
import BlocksPreview from './BlocksPreview';
import { aspectClass } from '@/lib/image-aspect';

const PhotoNode = ({ data, selected }: FlowNodeProps) => {
    const config = data.config || {};
    const hasImage = !!config.media_url;
    const aspect = aspectClass(config.image_aspect_ratio as ImageAspectRatio | undefined);

    return (
        <div
            className={`
                bg-white rounded-lg border-l-[3px] border-l-indigo-500 shadow-sm min-w-[260px] max-w-[300px]
                transition-all duration-150
                ${selected ? 'shadow-md ring-2 ring-blue-400/40' : 'border-t border-r border-b border-gray-200 hover:shadow-md'}
            `}
            style={{ borderTop: selected ? undefined : '1px solid #e5e7eb', borderRight: selected ? undefined : '1px solid #e5e7eb', borderBottom: selected ? undefined : '1px solid #e5e7eb' }}
        >
            {/* Header */}
            <div className="px-3 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-indigo-50 rounded-lg flex items-center justify-center">
                        <ImageIcon className="w-3.5 h-3.5 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="font-semibold text-[13px] text-gray-800 block truncate">{data.node_name || 'Foto'}</span>
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Imagem</span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-3 py-2.5">
                {hasImage ? (
                    <div className={`relative ${aspect} bg-gray-50 rounded-lg overflow-hidden border border-gray-100`}>
                        <img src={config.media_url} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                ) : (
                    <div className={`${aspect} bg-gray-50 rounded-lg flex items-center justify-center border border-dashed border-gray-200`}>
                        <div className="text-center">
                            <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-1" />
                            <p className="text-[11px] text-gray-400">Sem foto</p>
                        </div>
                    </div>
                )}
            </div>

            <BlocksPreview blocks={(config.blocks || []) as ContentBlock[]} />

            <div className="border-t border-gray-100 px-3 py-1.5 flex gap-1">
                <Button size="sm" variant="ghost" className="flex-1 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50/50 h-7 text-[11px]" onClick={() => data.onEdit?.(data.id)}>
                    <Settings className="w-3 h-3 mr-1" />
                    Editar
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-blue-500 hover:bg-blue-50/50 h-7 px-2" title="Duplicar" onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}>
                    <Copy className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500 hover:bg-red-50/50 h-7 px-2" onClick={() => data.onDelete?.(data.id)}>
                    <Trash2 className="w-3 h-3" />
                </Button>
            </div>

            <Handle type="target" position={Position.Left} id="target" className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            <Handle type="source" position={Position.Right} id="source" className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </div>
    );
};

export default memo(PhotoNode);
